import { Edit, OpenInNew } from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Link as LinkBase,
  Tooltip,
  List,
} from "@mui/material";
import { ResponsiveButtonLink } from "components/Button";
import { MainLayoutContainer } from "components/Layout";
import { TagManager } from "components/TagManager";
import { useUserDetail } from "./useUserDetail";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES } from "types/user";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { Link } from "react-router-dom";
import { UserItem } from "../../Organization/detail/components";

type UserInfoProps = {
  label: string;
  value: string | number | null | undefined;
  link?: string;
  tooltip?: string;
};

const UserInfo: React.FC<UserInfoProps> = ({ label, value, link, tooltip }) => (
  <Stack direction="row" spacing={2} sx={{ my: 1 }}>
    <Typography
      variant="subtitle1"
      fontWeight={600}
      sx={{ width: { sm: 150 } }}
    >
      {label}
    </Typography>
    <Typography variant="body1">{value ?? "N/A"}</Typography>
    {link && tooltip && (
      <LinkBase component={Link} to={link} target="_blank">
        <Tooltip title={tooltip}>
          <OpenInNew />
        </Tooltip>
      </LinkBase>
    )}
    {link && !tooltip && (
      <LinkBase component={Link} to={link} target="_blank">
        <OpenInNew />
      </LinkBase>
    )}
  </Stack>
);

export const UserDetail = () => {
  const {
    user,
    userId,
    loading,
    error,
    graduationClass,
    organizations,
    organization,
    mentors,
  } = useUserDetail();
  console.log(user);
  console.log(graduationClass);
  const { role } = useAuthUser();

  const canEdit = [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER].includes(
    role as ROLES,
  );

  return (
    <MainLayoutContainer
      title="User Details"
      loading={loading}
      error={error ? { message: error.message } : null}
      breadcrumb={BREADCRUMB_PAGE.USER}
      buttonActions={
        canEdit && user ? (
          <ResponsiveButtonLink
            variant="contained"
            to={`/user/${userId}/edit`}
            icon={<Edit />}
          >
            Edit User
          </ResponsiveButtonLink>
        ) : null
      }
    >
      {user && (
        <Box>
          <Stack direction={{ md: "row", xs: "column" }} spacing={5}>
            <Box mr={2}>
              <Box mt={2}>
                <Avatar
                  src={user.picture_url}
                  alt={user.name}
                  sx={{ width: 100, height: 100 }}
                />
              </Box>
            </Box>
            <Box>
              <Box>
                <UserInfo label="Name" value={user.name} />
                <UserInfo label="Role" value={user.role} />
                <UserInfo label="Email" value={user.email} />
                {graduationClass && user.role === ROLES.STUDENT && (
                  <UserInfo
                    label="Graduation Class"
                    value={graduationClass.name}
                    link={`/organization/${graduationClass.program?.org_id}/graduation-class/${graduationClass.graduation_class_id}`}
                    tooltip="View Graduation Class"
                  />
                )}
                {organization && user.role === ROLES.STUDENT && (
                  <UserInfo
                    label="Organization"
                    value={organization.name}
                    link={`/organization/${organization.org_id}`}
                    tooltip="View Organization"
                  />
                )}
                <UserInfo
                  label="Last Updated"
                  value={
                    user.updated_at
                      ? new Date(user.updated_at).toLocaleDateString()
                      : undefined
                  }
                />
              </Box>
              <Box width="100%" my={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Tag group:
                  </Typography>
                  <TagManager
                    tags={user.tags.map((t) => t.tag)}
                    isEditing={false}
                  />
                </Stack>
              </Box>
              {organizations.length > 0 && (
                <Typography variant="subtitle1" fontWeight={600}>
                  Organizations:
                </Typography>
              )}
              {organizations.map((org) => (
                <Typography sx={{ ml: 2, my: 1 }} key={org.name}>
                  - {org.name}
                </Typography>
              ))}
              {user.role === ROLES.STUDENT && (
                <>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mt: 2 }}
                  >
                    Mentors:
                  </Typography>
                  {mentors && mentors.length > 0 ? (
                    <List sx={{ ml: 1 }}>
                      {mentors.map((mentor) => (
                        <UserItem
                          key={mentor.user_id}
                          user={mentor}
                          displayEmailSendInvitation={false}
                        />
                      ))}
                    </List>
                  ) : (
                    <Typography sx={{ ml: 2, my: 1 }}>
                      No mentors assigned
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Stack>
        </Box>
      )}
    </MainLayoutContainer>
  );
};
